import React, { useEffect, useState } from "react";

export function installEnvironment(nav = navigator, standalone = false) {
  const ua = nav.userAgent || "";
  return {
    standalone: standalone || nav.standalone === true,
    android: /Android/i.test(ua),
    ios: /iPhone|iPad|iPod/i.test(ua) || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1),
    embedded: /WhatsApp|FBAN|FBAV|Instagram|; wv\)/i.test(ua),
  };
}

// Mounted outside authentication so a recipient can find installation guidance
// on the invitation page as well. Never redirects or exposes invitation tokens.
export function InstallApp() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const env = installEnvironment(navigator, window.matchMedia?.("(display-mode: standalone)").matches);
  useEffect(() => {
    const available = event => { event.preventDefault(); setPrompt(event); };
    const done = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", available);
    window.addEventListener("appinstalled", done);
    return () => {
      window.removeEventListener("beforeinstallprompt", available);
      window.removeEventListener("appinstalled", done);
    };
  }, []);
  if (installed || env.standalone || (!env.android && !env.ios && !env.embedded && !prompt && !message)) return null;
  const install = async () => {
    if (!prompt || busy) return;
    setBusy(true); setMessage("");
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      setMessage(choice?.outcome === "accepted"
        ? "Asennuspyyntö hyväksytty. Odota selaimen vahvistusta."
        : "Asennusta ei tehty. Voit käyttää ACAIta myös selaimessa.");
    } catch {
      setMessage("Asennusikkunaa ei voitu avata. Käytä alla olevia selaimen valikko-ohjeita.");
    } finally { setPrompt(null); setBusy(false); }
  };
  return <aside className="mx-auto w-full max-w-lg px-4 py-2" aria-label="Sovelluksen asentaminen">
    <details className="rounded-xl border border-border bg-card p-3 text-sm">
      <summary className="min-h-11 cursor-pointer py-3 font-semibold">Asenna ACAI / lisää aloitusnäyttöön</summary>
      <div className="space-y-3 pt-2 text-muted-foreground">
        <p>Erillistä sovelluskauppalatausta ei tarvita. Asennus ei luo käyttäjätiliä: viimeistele saamasi kutsu erikseen.</p>
        {env.embedded && <p className="text-amber-400">Olet mahdollisesti viestisovelluksen sisäisessä selaimessa. Avaa sen valikosta tämä sama linkki ulkoiseen selaimeen. Älä jaa henkilökohtaista kutsulinkkiä muille.</p>}
        {env.ios ? <p>Avaa linkki Safarissa → Jaa → Lisää Koti-valikkoon. Jos valinta näkyy, pidä Avaa verkkosovelluksena päällä.</p>
          : <p>Android: avaa tämä sama sivu Chromessa → ⋮ → Lisää aloitusnäyttöön → Asenna. Selaimesta riippuen valinnan nimi voi olla Asenna sovellus tai Luo pikakuvake. Jos valintaa ei näy, päivitä Chrome ja kokeile tavallisessa, ei yksityisessä välilehdessä.</p>}
        {prompt && <button type="button" disabled={busy} onClick={install} className="min-h-11 rounded-xl bg-primary px-4 py-2 font-semibold text-primary-foreground disabled:opacity-50">{busy ? "Avataan asennusta…" : "Asenna ACAI"}</button>}
        <p>Jos asennusvalintaa ei ole, ACAIta voi silti käyttää selaimessa. Asennusta ei voida käynnistää automaattisesti kaikissa selaimissa.</p>
        {message && <p role="status">{message}</p>}
      </div>
    </details>
  </aside>;
}
